/**
 * The Filters card shared by both Part Schedule tabs, so "From ERP" and
 * "Not from ERP" offer the same controls in the same places: a part search
 * (part number, description or supplier part no), a supplier search, the
 * supplier multi-select, and one status-style select whose meaning each tab
 * supplies (Active/Inactive on the ERP tab, Default/Manual firm days on the
 * other — the ERP tab's active flag has no meaning for parts without an entry).
 */
import { Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import SupplierMultiSelect from '../purchasing/SupplierMultiSelect';

export interface StatusOption {
  value: string;
  label: string;
}

interface PartScheduleFiltersCardProps {
  search: string;
  onSearchChange: (value: string) => void;
  supplierSearch: string;
  onSupplierSearchChange: (value: string) => void;
  supplierOptions: string[];
  supplierFilter: string[];
  onSupplierFilterChange: (selected: string[]) => void;
  statusLabel: string;
  statusValue: string;
  statusOptions: StatusOption[];
  onStatusChange: (value: string) => void;
}

export default function PartScheduleFiltersCard({
  search, onSearchChange, supplierSearch, onSupplierSearchChange,
  supplierOptions, supplierFilter, onSupplierFilterChange,
  statusLabel, statusValue, statusOptions, onStatusChange,
}: PartScheduleFiltersCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Filters</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[240px]">
            <label className="block text-xs font-medium text-muted-foreground">
              Search by part number, description or supplier part no
            </label>
            <div className="relative mt-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => onSearchChange(e.target.value)}
                placeholder="Part number, description or supplier part no…"
                className="pl-8"
              />
            </div>
          </div>
          <div className="flex-1 min-w-[240px]">
            <label className="block text-xs font-medium text-muted-foreground">
              Search by supplier
            </label>
            <div className="relative mt-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={supplierSearch}
                onChange={e => onSupplierSearchChange(e.target.value)}
                placeholder="Supplier name…"
                className="pl-8"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Supplier</label>
            <SupplierMultiSelect
              options={supplierOptions}
              selected={supplierFilter}
              onChange={onSupplierFilterChange}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground">{statusLabel}</label>
            <Select value={statusValue} onValueChange={onStatusChange}>
              <SelectTrigger className="w-[160px] mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
